import Alpine from 'alpinejs';
import { installPlayerFacade } from './player-facade.js';
import { initCarousels } from './carousels.js';
import { initSchedule } from './schedule.js';
import { initHome } from './home.js';
import { initSearch } from './search.js';
import { initProgrammeLatest } from './programme.js';
import { registerChartVote } from './charts.js';
import { registerContactForm } from './contact.js';
import { installLiveSchedule } from './live-schedule.js';

installPlayerFacade();

function bootHome() {
  initCarousels();
  initSchedule();
  initHome();
  initSearch();
  initProgrammeLatest();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootHome);
} else {
  bootHome();
}

// Global Alpine store for chrome UI state (menu drawer, open tab index).
document.addEventListener('alpine:init', () => {
  registerChartVote(Alpine);
  registerContactForm(Alpine);

  Alpine.store('chrome', {
    menuOpen: false,
    openTab: null,
    // Title of the currently open tab, shown in the mobile drill-down "back" header
    // (.prevBtnMenu span) — mirrors prod where opening a tab swaps the "go to site" link
    // for a back button labelled with the active section.
    activeTitle: '',
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      document.body.style.overflow = this.menuOpen ? 'hidden' : '';
    },
    closeMenu() {
      this.menuOpen = false;
      this.openTab = null;
      this.activeTitle = '';
      document.body.style.overflow = '';
    },
    setTab(i, title) {
      if (this.openTab === i) { this.openTab = null; this.activeTitle = ''; }
      else { this.openTab = i; this.activeTitle = title || ''; }
    },
    // Mobile "back" from an open tab to the main menu list (keeps the drawer open).
    closeTab() { this.openTab = null; this.activeTitle = ''; }
  });

  // Shared player state for the header live button + the strip player (app-player-new) + the
  // .wrapperPage padding. playing/current/next/time mirror the real player engine; expanded/
  // closed/volumeOpen are UI flags (Angular isExpanded / isPlayerClose).
  const fmt = sec => {
    if (!sec || !isFinite(sec)) return '0:00';
    sec = Math.round(sec);
    return Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
  };
  Alpine.store('playerUI', {
    playing: false,
    live: true,
    current: { title: '', desc: '', image: '', link: '', isLive: true },
    next: null,
    hasNext: false,
    currentTime: 0,
    duration: 0,
    seekable: false,
    expanded: true,
    closed: false,
    progress() { return this.duration ? (this.currentTime / this.duration) * 100 : 0; },
    currentTimeStr() { return fmt(this.currentTime); },
    durationStr() { return fmt(this.duration); },
    tag() { return this.current && this.current.isLive ? 'חי' : ''; },
    // header live button toggles the live stream; strip play/pause resumes the current item
    toggleLive() { window.GLZ.player.playLive(); },
    togglePlay() { window.GLZ.player.togglePlay(); },
    pause() { window.GLZ.player.pause(); },
    toggleExpand() { this.expanded = !this.expanded; },
    close() { window.GLZ.player.pause(); this.closed = true; },
    setVolume(v) { window.GLZ.player.setVolume(v); },
    seek(percent) { window.GLZ.player.seek(percent); }
  });

  // Seed the player from the strip's server-rendered live data (stream URL + current/next
  // programme), then mirror the engine state into the store on every change.
  const el = document.querySelector('[data-player]');
  if (el) {
    const d = el.dataset;
    if (d.liveUrl) window.GLZ.player.setLiveUrl(d.liveUrl);
    const liveItems = [];
    if (d.liveTitle || d.liveImage) liveItems.push({ id: 'live', title: d.liveTitle, desc: d.liveDesc, image: d.liveImage, link: d.liveLink, url: d.liveUrl, isLive: true });
    if (d.hasNext === 'true') liveItems.push({ id: 'live-next', title: d.nextTitle, desc: d.nextDesc, image: d.nextImage, link: d.nextLink, isLive: true });
    if (liveItems.length) window.GLZ.player.initLive(liveItems);

    // Live programme/song updates are scheduled, not polled: the server states when the next change
    // happens and the browser advances locally. See live-schedule.js.
    installLiveSchedule();
  } else {
    // chrome-only pages still carry the live URL on the header button
    const liveEl = document.querySelector('[data-live-url]');
    if (liveEl && liveEl.dataset.liveUrl) window.GLZ.player.setLiveUrl(liveEl.dataset.liveUrl);
  }

  window.GLZ.player.onState(s => {
    const p = Alpine.store('playerUI');
    p.playing = s.playing;
    p.live = s.live;
    if (s.current) p.current = s.current;
    p.next = s.next;
    p.hasNext = s.hasNext;
    p.currentTime = s.currentTime;
    p.duration = s.duration;
    p.seekable = s.seekable;
  });
});

window.Alpine = Alpine;
Alpine.start();
